import { useI18n } from "../i18n";
import { formatUSD } from "../lib/api";
import { TrendingUp, TrendingDown, MoveHorizontal, Gauge } from "lucide-react";

const LEVEL_LABEL_GAP = 17;

// Positions only the text labels; the level lines continue to use their raw y values.
export function resolveLevelLabelPositions(labels, gap = LEVEL_LABEL_GAP) {
  const positions = labels.map((label) => ({ ...label, labelY: label.lineY + label.offsetY }));
  const minY = Math.min(...positions.map(({ minY: value }) => value ?? 30));
  const maxY = Math.max(...positions.map(({ maxY: value }) => value ?? 224));

  for (let pass = 0; pass < positions.length * 2; pass += 1) {
    let changed = false;
    for (let left = 0; left < positions.length; left += 1) {
      for (let right = left + 1; right < positions.length; right += 1) {
        const a = positions[left];
        const b = positions[right];
        const overlapsHorizontally = a.xStart < b.xEnd && b.xStart < a.xEnd;
        if (!overlapsHorizontally || Math.abs(a.labelY - b.labelY) >= gap) continue;
        const move = (gap - Math.abs(a.labelY - b.labelY)) / 2;
        if (a.labelY <= b.labelY) { a.labelY -= move; b.labelY += move; }
        else { a.labelY += move; b.labelY -= move; }
        changed = true;
      }
    }
    positions.forEach((label) => { label.labelY = Math.max(minY, Math.min(maxY, label.labelY)); });
    if (!changed) break;
  }
  return positions;
}

/**
 * Lightweight SVG "picture" summarizing the AI technical analysis:
 *  - RSI gauge (0-100)
 *  - MA7 / MA30 chart with current price
 *  - Support / Resistance horizontal ladder with entry marker
 *  - Trend arrow badge
 *
 * Pure client-side render (no external requests) — zero extra network cost.
 */
export default function TechnicalChart({ symbol, analysis, livePrice }) {
  const { t } = useI18n();
  if (!analysis?.tech) {
    return (
      <div className="border border-border/60 rounded-sm bg-[#09090B] p-3 sm:p-5 shimmer h-[220px]" />
    );
  }
  const tech = analysis.tech;
  const rsi = Number.isFinite(tech.rsi_14) ? Math.max(0, Math.min(100, tech.rsi_14)) : null;
  const rsiColor = rsi >= 70 ? "#EF4444" : rsi <= 30 ? "#22C55E" : "#F59E0B";
  const price = livePrice || analysis.entry_price;

  // S/R ladder scaling
  const chartValues = [tech.support, tech.low_30d, price, analysis.entry_price, analysis.stop_loss, tech.resistance, tech.high_30d, analysis.target_price].filter(Number.isFinite);
  const priceMin = Math.min(...chartValues);
  const priceMax = Math.max(...chartValues);
  const pad = (priceMax - priceMin) * 0.05 || 1;
  const yMin = priceMin - pad;
  const yMax = priceMax + pad;
  const H = 240;
  const chartTop = 24;
  const chartBottom = H - 16;
  const yScale = (v) => chartBottom - ((v - yMin) / (yMax - yMin)) * (chartBottom - chartTop);

  const trend = tech.trend || "SIDEWAYS";
  const TrendIcon = trend === "UP" ? TrendingUp : trend === "DOWN" ? TrendingDown : MoveHorizontal;
  const trendColor = trend === "UP" ? "text-emerald-400" : trend === "DOWN" ? "text-red-400" : "text-amber-400";

  const priceInZone = price >= tech.support && price <= tech.resistance;

  const rsiCirc = 100 * Math.PI; // approx for r=50, gauge is half circle
  const dash = ((rsi || 0) / 100) * 200; // 200 = length for 180deg arc at r~64

  const levelLabels = resolveLevelLabelPositions([
    { key: "resistance", lineY: yScale(tech.resistance), offsetY: -5, xStart: 8, xEnd: 190, x: 10, anchor: "start", color: "#EF4444", text: `${t("lbl_resistance")} ${formatUSD(tech.resistance)}` },
    { key: "support", lineY: yScale(tech.support), offsetY: -5, xStart: 8, xEnd: 190, x: 10, anchor: "start", color: "#22C55E", text: `${t("lbl_support")} ${formatUSD(tech.support)}` },
    { key: "target", lineY: yScale(analysis.target_price), offsetY: -4, xStart: 210, xEnd: 392, x: 370, anchor: "end", color: "#10B981", text: `${t("lbl_target")} ${formatUSD(analysis.target_price)}` },
    { key: "stop", lineY: yScale(analysis.stop_loss), offsetY: -4, xStart: 210, xEnd: 392, x: 370, anchor: "end", color: "#EF4444", text: `${t("lbl_stop")} ${formatUSD(analysis.stop_loss)}` },
    { key: "entry", lineY: yScale(analysis.entry_price), offsetY: -5, xStart: 105, xEnd: 295, x: 200, anchor: "middle", color: "#22D3EE", text: `${t("lbl_entry")} ${formatUSD(analysis.entry_price)}` },
    { key: "current", lineY: yScale(price), offsetY: -7, xStart: 205, xEnd: 392, x: 350, anchor: "end", color: "#FFFFFF", text: `${t("lbl_now")} ${formatUSD(price)}` },
  ], LEVEL_LABEL_GAP);

  return (
    <div className="border border-border/60 rounded-sm bg-[#09090B] p-3 sm:p-5 overflow-x-hidden" data-testid="technical-chart-picture">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-sm bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
            <Gauge className="w-3.5 h-3.5 text-emerald-400" strokeWidth={1.5} />
          </div>
          <span className="font-display font-bold text-base sm:text-lg leading-tight">{t("tech_snapshot")}</span>
          <span className="text-[11px] font-mono text-zinc-400 uppercase tracking-widest">{symbol}</span>
        </div>
        <div className={`flex items-center gap-1.5 px-2 py-1 rounded-sm border border-border/60 ${trendColor}`}>
          <TrendIcon className="w-3 h-3" strokeWidth={2} />
          <span className="text-[11px] sm:text-xs font-mono uppercase tracking-widest leading-relaxed">{t("trend")} · {t(trend) || trend}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {/* RSI GAUGE */}
        <div className="border border-border/60 rounded-sm p-3 flex flex-col items-center">
          <div className="text-xs sm:text-sm font-mono uppercase tracking-widest text-zinc-300 mb-2">{t("rsi_label")}</div>
          <svg viewBox="0 0 160 100" width="100%" height="120" aria-label={`${t("rsi_label")} ${rsi === null ? "—" : rsi.toFixed(1)}`}>
            {/* Background arc */}
            <path d="M 20 90 A 60 60 0 0 1 140 90" stroke="#27272A" strokeWidth="12" fill="none" strokeLinecap="round" />
            {/* Value arc */}
            <path
              d="M 20 90 A 60 60 0 0 1 140 90"
              stroke={rsiColor}
              strokeWidth="12"
              fill="none"
              strokeLinecap="round"
              strokeDasharray={`${dash} 200`}
              style={{ transition: "stroke-dasharray 500ms ease" }}
            />
            {/* Ticks */}
            <text x="20" y="98" fontSize="8" fill="#52525B" fontFamily="JetBrains Mono">0</text>
            <text x="76" y="18" fontSize="8" fill="#52525B" fontFamily="JetBrains Mono">50</text>
            <text x="132" y="98" fontSize="8" fill="#52525B" fontFamily="JetBrains Mono">100</text>
          </svg>
          <div className="font-mono text-3xl sm:text-4xl font-bold leading-tight" style={{ color: rsiColor }}>{rsi === null ? "—" : rsi.toFixed(1)}</div>
          <div className="text-xs sm:text-sm text-zinc-300 font-mono mt-1 text-center leading-relaxed">
            {rsi === null ? t("status_unavailable") : rsi >= 70 ? t("rsi_over") : rsi <= 30 ? t("rsi_under") : t("rsi_neutral")}
          </div>
        </div>

        {/* S/R LADDER */}
        <div className="border border-border/60 rounded-sm p-3">
          <div className="text-xs sm:text-sm font-mono uppercase tracking-widest text-zinc-300 mb-2">{t("levels")}</div>
          <svg viewBox={`0 0 400 ${H}`} width="100%" height={H} preserveAspectRatio="none" aria-label={t("levels")}>
            {/* Grid */}
            {[0, 0.25, 0.5, 0.75, 1].map((f) => (
              <line key={f} x1="0" x2="400" y1={chartTop + (chartBottom - chartTop) * f} y2={chartTop + (chartBottom - chartTop) * f} stroke="#27272A" strokeOpacity="0.4" strokeDasharray="2 3" />
            ))}
            {/* Resistance */}
            <line x1="0" x2="400" y1={yScale(tech.resistance)} y2={yScale(tech.resistance)} stroke="#EF4444" strokeWidth="1.2" strokeDasharray="4 4" />
            {/* Support */}
            <line x1="0" x2="400" y1={yScale(tech.support)} y2={yScale(tech.support)} stroke="#22C55E" strokeWidth="1.2" strokeDasharray="4 4" />
            {/* Target */}
            <line x1="0" x2="400" y1={yScale(analysis.target_price)} y2={yScale(analysis.target_price)} stroke="#10B981" strokeWidth="0.8" strokeOpacity="0.7" />
            {/* Stop */}
            <line x1="0" x2="400" y1={yScale(analysis.stop_loss)} y2={yScale(analysis.stop_loss)} stroke="#EF4444" strokeWidth="0.8" strokeOpacity="0.7" />
            {/* Entry */}
            <line x1="0" x2="400" y1={yScale(analysis.entry_price)} y2={yScale(analysis.entry_price)} stroke="#22D3EE" strokeWidth="1.8" />
            {/* Current price marker */}
            <line x1="0" x2="400" y1={yScale(price)} y2={yScale(price)} stroke="#FFFFFF" strokeWidth="1.2" />
            <circle cx="360" cy={yScale(price)} r="5" fill="#FFFFFF" />
            {levelLabels.map((label) => (
              <g key={label.key} data-testid={`level-label-${label.key}`}>
                <line x1={label.x} x2={label.x} y1={label.lineY} y2={label.labelY + 3} stroke={label.color} strokeOpacity="0.65" strokeWidth="0.8" />
                <text x={label.x} y={label.labelY} fill={label.color} fontSize={label.key === "entry" ? "14" : label.key === "resistance" || label.key === "support" ? "13" : "12"} fontFamily="JetBrains Mono" textAnchor={label.anchor} fontWeight={label.key === "entry" ? "bold" : "600"} paintOrder="stroke" stroke="#09090B" strokeWidth="4" strokeLinejoin="round">{label.text}</text>
              </g>
            ))}
          </svg>
        </div>

        {/* Moving Averages / Metadata */}
        <div className="border border-border/60 rounded-sm p-3 space-y-3">
          <div className="text-xs sm:text-sm font-mono uppercase tracking-widest text-zinc-300">{t("moving_avgs")}</div>
          <MetaRow label="MA(7)" value={formatUSD(tech.ma7)} color={price >= tech.ma7 ? "text-emerald-400" : "text-red-400"} />
          <MetaRow label="MA(30)" value={formatUSD(tech.ma30)} color={price >= tech.ma30 ? "text-emerald-400" : "text-red-400"} />
          <MetaRow label="SMA50" value={formatUSD(tech.sma50)} color={price >= tech.sma50 ? "text-emerald-400" : "text-red-400"} />
          <MetaRow label="SMA200" value={formatUSD(tech.sma200)} color={price >= tech.sma200 ? "text-emerald-400" : "text-red-400"} />
          <div className="border-t border-border/60 pt-2 text-[10px] font-mono leading-relaxed">
            <div className="flex justify-between gap-2"><span className="text-zinc-400">{t("cross_regime")}</span><span className="text-amber-300 text-right">{tech.cross_event || "—"} · {tech.market_regime || "—"}</span></div>
          </div>
          <div className="border-t border-border/60 pt-2">
            <MetaRow label={t("high_30d_label")} value={formatUSD(tech.high_30d)} color="text-zinc-300" />
            <MetaRow label={t("low_30d_label")} value={formatUSD(tech.low_30d)} color="text-zinc-300" />
          </div>
          <div className={`mt-3 rounded-sm px-2 py-2 text-xs font-mono uppercase tracking-widest leading-relaxed ${priceInZone ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/40" : "bg-white/5 text-zinc-400 border border-border/60"}`}>
            {priceInZone ? t("price_in_sr") : t("price_out_sr")}
          </div>
        </div>
      </div>
    </div>
  );
}

function MetaRow({ label, value, color }) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm leading-relaxed">
      <span className="text-zinc-400 font-mono uppercase text-xs tracking-wider">{label}</span>
      <span className={`font-mono text-sm sm:text-base text-right ${color}`}>{value}</span>
    </div>
  );
}
